import React, { useState } from 'react';
import {
  Box, Button, Typography, TextField, Paper,
  LinearProgress, Grid
} from '@mui/material';
import { CONFIG_APP_UPTIME_METRICS_BASE_URL } from '../../../configApp';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useSnackbar } from 'notistack';
import { AnimatePresence, motion } from "framer-motion/dist/framer-motion";
import CheckIcon from '@mui/icons-material/Check';
import { ArrowBackIos, ArrowForwardIos, Check } from '@mui/icons-material';

const steps = [
  "What would you like to host with Oikos?",
  "Which role describes you the best?",
  "What is the size of your team or organization?",
  "How did you hear about us?",
  "Almost done — Let's connect again!"
];

const optionsList = [
  ["Website", "Database", "Web Service", "Marketplace", "Stand Alone app", "Something else"],
  ["Developer", "Small business owner", "Founder", "Marketer", "Student", "Other"],
  ["Just me", "2-9", "10-30", "31-100", "100+"],
  ["X (Formerly Twitter)", "LinkedIn", "YouTube", "Friend", "Product Hunt", "Newsletter", "Search engine", "Other"],
  null
];

const progressMessages = [
  "Let’s get to know you 👋",
  "Great! Now let’s learn about your role 💼",
  "Tell us about your team 👥",
  "Just curious — how’d you find us? 🔍",
  "Almost done! Just one last thing! 🎉"
];

const Onboarding = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [url, setUrl] = useState("");
  const [urlError, setUrlError] = useState(false);
  const [urlHelperText, setUrlHelperText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const validateUrl = (input) => {
    const linkedinRegex = /^https?:\/\/(www\.)?linkedin\.com\/.+$/i;
    const githubRegex = /^https?:\/\/(www\.)?github\.com\/.+$/i;

    if (!input) {
      setUrlError(true);
      setUrlHelperText("Please enter your LinkedIn or GitHub URL.");
      return false;
    }

    if (!linkedinRegex.test(input) && !githubRegex.test(input)) {
      setUrlError(true);
      setUrlHelperText(
        <>
          Please enter a valid LinkedIn or GitHub URL.
          <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
            <li>https://www.linkedin.com/in/yourname</li>
            <li>https://github.com/yourusername</li>
          </ul>
        </>
      );
      return false;
    }

    setUrlError(false);
    setUrlHelperText("");
    return true;
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      if (!validateUrl(url)) return;
      setSubmitting(true);
      const payload = {
        id: localStorage.getItem("userId"),
        ...answers,
        contactUrl: url
      };

      fetch(`${CONFIG_APP_UPTIME_METRICS_BASE_URL}/api/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(response => {
          if (!response.ok) throw new Error('Failed to submit');
          return response.json();
        })
        .then(data => {
          enqueueSnackbar("Hey there👋 Welcome to Oikos!", {
            variant: 'success',
            autoHideDuration: 5000,
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
          });
          history.push("/teamboard");
          setAnswers({});
          setUrl("");
          setActiveStep(0);
        })
        .catch(err => {
          console.error(err);
          alert("There was an error submitting your data.");
        })
        .finally(() => setSubmitting(false));
    } else {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => setActiveStep(prev => prev - 1);

  const handleOptionClick = (value) => {
    setAnswers({ ...answers, [activeStep]: value });
  };

  const progress = ((activeStep + 1) / steps.length) * 100;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100vw',
        height: '100vh',
        overflowY: 'auto',
        background: 'radial-gradient(circle at 20% 30%, rgba(0, 30, 162, 0.4), transparent 60%), radial-gradient(circle at 80% 70%, rgba(0, 94, 255, 0.3), transparent 60%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 0,
      }}
    >

      <Paper
        variant='outlined'
        sx={{
          width: '100%',
          maxWidth: '40rem',
          p: 4,
          borderRadius: 3,
          backdropFilter: 'blur(10px)',
          color: 'text.primary',
          backgroundColor: 'transparent',
          boxShadow: "rgba(17, 12, 46, 0.05) 0px 30px 50px 5px"
        }}
      >
        {activeStep === 0 && (
          <Box sx={{ textAlign: 'left', mb: 3 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Grid container alignItems="center" direction="row" justifyContent="space-between">
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  Welcome to Oikos!
                </Typography>
                <motion.div
                  initial={{ opacity: 0, scale: 0.3 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{
                    type: "spring",
                    stiffness: 260,
                    damping: 20,
                    delay: 0.2
                  }}
                >

                  <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 256 256" height="2rem" width="2rem" xmlns="http://www.w3.org/2000/svg"><path d="M111.49,52.63a15.8,15.8,0,0,0-26,5.77L33,202.78A15.83,15.83,0,0,0,47.76,224a16,16,0,0,0,5.46-1l144.37-52.5a15.8,15.8,0,0,0,5.78-26Zm-8.33,135.21-35-35,13.16-36.21,58.05,58.05Zm-55,20,14-38.41,24.45,24.45ZM156,168.64,87.36,100l13-35.87,91.43,91.43ZM160,72a37.8,37.8,0,0,1,3.84-15.58C169.14,45.83,179.14,40,192,40c6.7,0,11-2.29,13.65-7.21A22,22,0,0,0,208,23.94,8,8,0,0,1,224,24c0,12.86-8.52,32-32,32-6.7,0-11,2.29-13.65,7.21A22,22,0,0,0,176,72.06,8,8,0,0,1,160,72ZM136,40V16a8,8,0,0,1,16,0V40a8,8,0,0,1-16,0Zm101.66,82.34a8,8,0,1,1-11.32,11.31l-16-16a8,8,0,0,1,11.32-11.32Zm4.87-42.75-24,8a8,8,0,0,1-5.06-15.18l24-8a8,8,0,0,1,5.06,15.18Z"></path></svg>
                </motion.div>
              </Grid>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, }}
            >
              <Typography variant="subtitle1" sx={{ color: 'text.secondary', mt: 1 }}>
                We're excited you're here — let's personalize your experience.
              </Typography>
            </motion.div>
          </Box>)}

        <Box sx={{ mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#1976d2'
              }
            }}
          />
        </Box>

        <Box key={activeStep}>
          <Typography variant="h6" sx={{ mb: 2 }} gutterBottom>
            {steps[activeStep]}
          </Typography>

          {activeStep < steps.length - 1 ? (
            <Grid container spacing={2}>
              {optionsList[activeStep].map((option, idx) => (
                <Grid item xs={12} md={6} key={option}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <Button
                      fullWidth
                      variant={answers[activeStep] === option ? 'outlined' : 'text'}
                      onClick={() => handleOptionClick(option)}
                      sx={{
                        py: 2,
                        justifyContent: "space-between",
                        textTransform: 'none'
                      }}
                    >
                      {option}
                      {answers[activeStep] === option && <CheckIcon sx={{ color: 'primary.main' }} />}
                    </Button>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          ) : (
            <TextField
              fullWidth
              label="LinkedIn or GitHub URL"
              variant="outlined"
              value={url}
              placeholder='Enter your LinkedIn or GitHub URL'
              onChange={(e) => {
                const inputValue = e.target.value;
                setUrl(inputValue);
                validateUrl(inputValue); // live validation
              }}
              error={urlError}
              helperText={urlHelperText}
            />
          )}
        </Box>

        <Box mt={4} display="flex" justifyContent="space-between">
          <Button disabled={activeStep === 0 || submitting} onClick={handleBack}>
            Previous
          </Button>
          <Button
            variant="contained"
            endIcon={<ArrowForwardIos />}
            onClick={handleNext}
            disabled={
              submitting ||
              (activeStep < steps.length - 1 && !answers[activeStep]) ||
              (activeStep === steps.length - 1 && (!url || urlError))
            }
          >
            {activeStep === steps.length - 1
              ? submitting ? "Submitting..." : "Submit"
              : "Next"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Onboarding;
